import './CroOnboard.scss';
import { FormEvent, useState } from 'react';
import { Form } from 'react-bootstrap';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useSetError } from '@src/redux/modals';
import { reportError } from '@src/utils/errorReporting';
import { CroOnboardLambdaFetch } from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';

const CHECK_BUCKET_NAME_REGEX = /^[\w-]+\.[\w-]+\.[\w-]{2,4}$/;

const checkBucketName = (url: string): boolean => {
  if (!url) return false;
  return CHECK_BUCKET_NAME_REGEX.test(url);
};

export const CroOnboard = () => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const [inputText, setInputText] = useState('');
  const [prodEnabled, setProdEnabled] = useState(false);
  const [subdomainSubmitted, setSubdomainSubmitted] = useState('');

  const clearAllFields = () => {
    setInputText('');
    setProdEnabled(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!checkBucketName(inputText)) {
      setError({ message: 'Incorrect bucket name' });
      return;
    }
    // Not awaited: onboarding can outlast the gateway timeout and reports its result to Slack.
    CroOnboardLambdaFetch('/add', {
      params: { s3_bucket_name: inputText, prod_env: `${prodEnabled}`, partner_name: cid },
      retries: 0
    }).catch(err => reportError(err, { operation: 'CroOnboard.add' }));
    setSubdomainSubmitted(inputText);
    clearAllFields();
  };

  return (
    <Page title='CRO Onboard' pageType='CRO Onboard' className='cro-onboard-page'>
      <div className='cro-onboard-body'>
        <WidgetContainer className='cro-onboard-card' header='Onboard a client subdomain'>
          <div className='cro-onboard-content'>
            <div className='cro-onboard-description'>
              <p>
                This tool onboards a client subdomain into the AMP AWS account by creating and
                configuring S3 buckets and CloudFront distributions. It also requests an SSL
                certificate from the client.
              </p>
              <p>
                Completing the setup needs both client actions and AMP Engineering manual actions.
              </p>
            </div>

            <Form className='cro-onboard-form' onSubmit={handleSubmit}>
              <Form.Group controlId='cro-onboard-subdomain'>
                <Form.Label>Production Subdomain Name*</Form.Label>
                <Form.Control
                  data-testid='subdomain-input'
                  value={inputText}
                  placeholder='e.g. get.test-partner.com'
                  onChange={e => setInputText(e.target.value)}
                />
                <Form.Text>
                  Enter the subdomain only, e.g. <code>get.insiderenvy.com</code>
                </Form.Text>
              </Form.Group>

              <div className='cro-onboard-actions'>
                <ToggleSwitch
                  data-testid='prod-only-toggle'
                  label='Production Only'
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  checked={prodEnabled}
                  onChange={setProdEnabled}
                />
                <div className='cro-onboard-buttons'>
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    design={Design.NEUTRAL}
                    onClick={clearAllFields}
                  >
                    Clear
                  </Button>
                  <Button type='submit' size={Size.LG} disabled={!inputText || !cid}>
                    Submit
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </WidgetContainer>
      </div>

      <AlertDialog
        intent={Intent.INFO}
        dataTestId='cro-onboard-dialog'
        show={subdomainSubmitted.length > 0}
        onHide={() => setSubdomainSubmitted('')}
        title='Onboarding In Progress'
        body={
          <>
            CRO onboarding for <b>{subdomainSubmitted}</b> is in progress; check the #cyb-testing
            Slack channel for status.
          </>
        }
        primaryAction={{ design: Design.NEUTRAL }}
      />
    </Page>
  );
};
